export const LEGAL_NAV = [
  { slug: "privacy-policy", title: "Privacy Policy" },
  { slug: "affiliate-disclosure", title: "Affiliate Disclosure" },
  { slug: "terms-of-service", title: "Terms of Service" },
  { slug: "refund-policy", title: "Refund Policy" },
  { slug: "cookie-policy", title: "Cookie Policy" },
];

const CONTACT = "therealcoman@yahoo.com";

export const LEGAL_PAGES = {
  "privacy-policy": {
    title: "Privacy Policy",
    updated: "June 1, 2026",
    intro:
      "This Privacy Policy explains how ShoppinMagic (\"we\", \"us\", \"our\") collects, uses, and protects information when you visit our website. ShoppinMagic is an affiliate marketplace — we do not sell products ourselves. We earn commissions through affiliate links and advertising, and this policy describes exactly how that works with respect to your data.",
    sections: [
      {
        id: "introduction",
        heading: "1. Introduction",
        paragraphs: [
          "ShoppinMagic is an affiliate marketplace and product discovery platform. We do not sell, stock, or ship any products. When you click a product link on our site, you are redirected to an external merchant (such as Amazon, Temu, or AliExpress) that handles the entire transaction.",
          "By using this website, you agree to the collection and use of information in accordance with this policy.",
        ],
      },
      {
        id: "data-we-collect",
        heading: "2. Information We Collect",
        paragraphs: ["We collect the following categories of information:"],
        bullets: [
          "IP address — used for security, analytics, and approximate regional content.",
          "Browser type and version — to optimize compatibility and performance.",
          "Device type and operating system — to deliver a mobile-friendly or desktop-friendly experience.",
          "Pages visited and time spent — to understand which products and guides are most useful.",
          "Click behavior — including outbound clicks on affiliate links and buttons, used to attribute commissions and improve recommendations.",
          "Referral source — the site or search engine that brought you here.",
          "Information you provide voluntarily — your email address when subscribing to our newsletter, and any details submitted through our contact or seller application forms.",
        ],
      },
      {
        id: "how-we-use-data",
        heading: "3. How We Use Your Information",
        bullets: [
          "Improving site performance — faster load times, better layouts, fewer broken experiences.",
          "Personalizing content — showing categories, deals, and products more relevant to your interests.",
          "Serving ads — including personalized advertising through Google AdSense and its partners.",
          "Tracking affiliate conversions — so merchants and affiliate networks can credit us when a purchase is made through our links.",
          "Communicating with you — sending our newsletter (only if you subscribe) and responding to your messages.",
          "Security and fraud prevention.",
        ],
      },
      {
        id: "google-adsense",
        heading: "4. Google AdSense & Third-Party Advertising Cookies",
        paragraphs: [
          "This website displays ads served by Google AdSense and other third-party advertising vendors. Third-party vendors, including Google, use cookies to serve ads based on your prior visits to this website or other websites on the internet.",
          "Google's use of advertising cookies — including the DoubleClick DART cookie — enables it and its partners to serve ads to you based on your visit to this site and/or other sites. The DART cookie does not collect personally identifiable information such as your name, email, or address.",
          "You can learn more about how Google uses information from sites that use its services at Google's \"Privacy & Terms\" page (policies.google.com/technologies/partner-sites), and you can view the list of AdSense-certified third-party vendors at support.google.com/adsense.",
        ],
      },
      {
        id: "opt-out",
        heading: "5. How to Opt Out of Personalized Advertising",
        bullets: [
          "Google Ads Settings — visit adssettings.google.com to turn off ad personalization from Google.",
          "AboutAds — visit www.aboutads.info/choices to opt out of personalized ads from many participating third-party ad networks at once.",
          "Network Advertising Initiative — visit optout.networkadvertising.org to manage preferences across NAI member companies.",
          "Mobile apps — use the DAA's AppChoices tool to control personalized advertising in apps.",
          "Browser controls — block or delete cookies in your browser settings; you can also enable \"Do Not Track\", though not all services honor it.",
          "On this site — use the cookie consent banner (shown on your first visit) to accept, limit to essential cookies, or decline. You can reset your choice by clearing this site's stored data in your browser.",
        ],
        paragraphs: [
          "If you opt out, you will still see ads — they simply won't be personalized based on your browsing behavior.",
        ],
      },
      {
        id: "analytics",
        heading: "6. Analytics (Google Analytics)",
        paragraphs: [
          "We use Google Analytics to understand how visitors use the site — which pages are popular, how long visits last, and where traffic comes from. Google Analytics sets cookies and may process your IP address (we enable IP anonymization where available).",
          "You can opt out of Google Analytics across all websites by installing Google's official opt-out browser add-on at tools.google.com/dlpage/gaoptout.",
        ],
      },
      {
        id: "affiliate-tracking",
        heading: "7. Affiliate Links, Tracking Pixels & Conversion Tracking",
        paragraphs: [
          "Our pages contain affiliate links. When you click one, the merchant or affiliate network may place a cookie or use a tracking pixel (a tiny invisible image) to record that you arrived from ShoppinMagic. If you make a purchase within the network's attribution window, that cookie allows us to be credited with a commission.",
          "This tracking is governed by the privacy policies of the respective affiliate networks and merchants, including Amazon Associates, Temu Affiliate, AliExpress Affiliate, CJ Affiliate, Impact, and ShareASale. We do not receive your payment details or full order information — typically only an anonymized record that a qualifying purchase occurred.",
        ],
      },
      {
        id: "third-parties",
        heading: "8. Third-Party Vendors & Links",
        paragraphs: [
          "Our website links to third-party merchants and uses third-party services (advertising, analytics, email delivery). We are not responsible for the privacy practices of these third parties. We encourage you to read the privacy policy of every site you visit.",
        ],
      },
      {
        id: "retention-security",
        heading: "9. Data Retention & Security",
        paragraphs: [
          "We retain personal information (such as newsletter emails and contact form messages) only as long as needed for the purposes described above, after which it is deleted. We use reasonable administrative and technical safeguards, but no method of transmission over the internet is 100% secure.",
        ],
      },
      {
        id: "gdpr",
        heading: "10. Your Rights Under GDPR (EEA & UK Visitors)",
        bullets: [
          "Right of access — request a copy of the personal data we hold about you.",
          "Right to rectification — ask us to correct inaccurate data.",
          "Right to erasure — ask us to delete your personal data (\"right to be forgotten\").",
          "Right to restrict or object to processing — including objecting to personalized advertising.",
          "Right to data portability — receive your data in a structured, machine-readable format.",
          "Right to withdraw consent at any time, without affecting prior lawful processing.",
          "Right to lodge a complaint with your local data protection authority.",
        ],
        paragraphs: [
          `To exercise any of these rights, contact us at ${CONTACT}. We respond within 30 days.`,
        ],
      },
      {
        id: "ccpa",
        heading: "11. Your Rights Under CCPA/CPRA (California Residents)",
        bullets: [
          "Right to know — what personal information we collect, use, and disclose.",
          "Right to delete — request deletion of your personal information, subject to legal exceptions.",
          "Right to correct inaccurate personal information.",
          "Right to opt out of the sale or sharing of personal information — note: we do NOT sell your personal information, and we do not share it for cross-context behavioral advertising beyond the cookie-based ad serving described above, which you can decline via the cookie banner.",
          "Right to non-discrimination — exercising your rights will not affect the content or prices you see.",
        ],
        paragraphs: [
          `California residents may submit requests via ${CONTACT} or through an authorized agent.`,
        ],
      },
      {
        id: "coppa",
        heading: "12. Children's Privacy (COPPA)",
        paragraphs: [
          "This website is not directed at children under 13, and we do not knowingly collect personal information from children under 13. If you believe a child has provided us personal information, contact us and we will delete it promptly.",
        ],
      },
      {
        id: "changes",
        heading: "13. Changes to This Policy",
        paragraphs: [
          "We may update this Privacy Policy from time to time. The \"Last updated\" date at the top reflects the latest revision. Material changes will be highlighted on this page.",
        ],
      },
      {
        id: "contact",
        heading: "14. Contact Us",
        paragraphs: [
          `Questions about this policy or your data? Email us at ${CONTACT} or use the form on our Contact page.`,
        ],
      },
    ],
  },

  "affiliate-disclosure": {
    title: "Affiliate Disclosure",
    updated: "June 1, 2026",
    intro:
      "Transparency matters to us. This disclosure explains how ShoppinMagic earns money, in plain language and in compliance with the U.S. Federal Trade Commission (FTC) guidelines on endorsements and testimonials (16 CFR Part 255).",
    sections: [
      {
        id: "affiliate-links",
        heading: "1. This Website Contains Affiliate Links",
        paragraphs: [
          "ShoppinMagic is an affiliate marketplace. Many of the links on this site — in product listings, comparison tables, blog posts, and buying guides — are affiliate links. This means that when you click a link and/or make a purchase on the merchant's website, we may earn a commission.",
        ],
      },
      {
        id: "no-extra-cost",
        heading: "2. No Extra Cost to You",
        paragraphs: [
          "Using our affiliate links never costs you anything extra. The price you pay is exactly the same whether you arrive through our link or visit the merchant directly. Commissions are paid by the merchant or affiliate network out of their own margin — never added to your price.",
        ],
      },
      {
        id: "networks",
        heading: "3. Affiliate Programs & Networks We Participate In",
        bullets: [
          "Amazon Associates — as an Amazon Associate, we earn from qualifying purchases.",
          "Temu Affiliate Program",
          "AliExpress Affiliate Program",
          "CJ Affiliate (Commission Junction)",
          "Impact (Impact.com partnership platform)",
          "ShareASale",
          "Other reputable affiliate networks and direct merchant programs, which may change over time.",
        ],
      },
      {
        id: "not-a-seller",
        heading: "4. We Do Not Sell Products",
        paragraphs: [
          "ShoppinMagic does not sell, stock, or ship any products. All product pricing, shipping, fulfillment, delivery, and customer service are handled entirely by the external merchants we link to. We do not control inventory, shipping times, product availability, or refunds, and we cannot intervene in any order you place with a merchant.",
        ],
      },
      {
        id: "editorial-independence",
        heading: "5. Editorial Independence",
        paragraphs: [
          "Commissions never influence which products we feature or how we rank them. Our recommendations are based on ratings, verified reviews, value, and popularity. If a placement is ever sponsored by a merchant, it will be clearly labeled as \"Sponsored\".",
        ],
      },
      {
        id: "ftc",
        heading: "6. FTC Compliance Statement",
        paragraphs: [
          "In accordance with FTC guidelines, please assume that any link on this website leading to products or services is an affiliate link from which we may receive compensation. All opinions expressed remain our own, and we only feature products we believe offer genuine value.",
        ],
      },
      {
        id: "questions",
        heading: "7. Questions?",
        paragraphs: [`Email us anytime at ${CONTACT} — we're happy to explain how any link or recommendation works.`],
      },
    ],
  },

  "terms-of-service": {
    title: "Terms of Service",
    updated: "June 1, 2026",
    intro:
      "These Terms of Service (\"Terms\") govern your use of the ShoppinMagic website. By accessing or using the site, you agree to be bound by these Terms. If you do not agree, please do not use the site.",
    sections: [
      {
        id: "the-service",
        heading: "1. What ShoppinMagic Is (and Is Not)",
        paragraphs: [
          "ShoppinMagic is an affiliate marketplace and product discovery platform. We are NOT a seller, retailer, or merchant. We do not list products for sale, process payments, hold inventory, or fulfill orders. All products featured on this site are sold and fulfilled by independent third-party merchants.",
        ],
      },
      {
        id: "third-party-transactions",
        heading: "2. Third-Party Merchants Handle Everything",
        paragraphs: [
          "All pricing, product descriptions, availability, shipping, fulfillment, returns, refunds, and customer service are the sole responsibility of the external merchants. We do not guarantee product quality, accuracy of listings, delivery times, or merchant reliability. Any transaction you make is a contract between you and the merchant — not with ShoppinMagic.",
          "Prices and availability shown on our site can change at any time on the merchant's site. The price and terms displayed at checkout on the merchant's website always apply.",
        ],
      },
      {
        id: "affiliate-relationships",
        heading: "3. Affiliate Relationships & Advertising",
        paragraphs: [
          "We earn commissions through affiliate links and display advertising (including Google AdSense). See our Affiliate Disclosure for full details. Your use of the site constitutes acknowledgment of these relationships.",
        ],
      },
      {
        id: "user-responsibilities",
        heading: "4. Your Responsibilities",
        bullets: [
          "Provide accurate information in any form you submit (newsletter, contact, seller applications).",
          "Use the website only for lawful purposes.",
          "Do not scrape, crawl, data-mine, or use bots/automation to extract content or data from the site without written permission.",
          "Do not attempt to interfere with the site's operation, security, or infrastructure.",
          "Do not misrepresent your identity or affiliation.",
        ],
      },
      {
        id: "intellectual-property",
        heading: "5. Intellectual Property",
        paragraphs: [
          "The ShoppinMagic name, logo, brand elements, site design, text, graphics, and original content are our property (or licensed to us) and are protected by copyright and trademark laws. Product names, images, and trademarks belong to their respective merchants and brands. You may view and share links to our content for personal, non-commercial use; any other use requires our written consent.",
        ],
      },
      {
        id: "disclaimers",
        heading: "6. Disclaimers",
        paragraphs: [
          "The website and all content are provided \"as is\" and \"as available\" without warranties of any kind, express or implied, including accuracy, merchantability, or fitness for a particular purpose. Product information is provided by merchants and affiliate networks and may contain errors.",
        ],
      },
      {
        id: "limitation-of-liability",
        heading: "7. Limitation of Liability",
        paragraphs: [
          "To the fullest extent permitted by law, ShoppinMagic and its owners shall not be liable for any damages arising from: products or services purchased from third-party merchants; actions, errors, or omissions of merchants or affiliate networks (including tracking failures and lost commissions); interruptions or errors on this website; or any indirect, incidental, or consequential damages. Our total aggregate liability shall not exceed one hundred US dollars (US $100).",
        ],
      },
      {
        id: "indemnification",
        heading: "8. Indemnification",
        paragraphs: [
          "You agree to indemnify and hold harmless ShoppinMagic from any claims, damages, or expenses arising from your misuse of the website or violation of these Terms.",
        ],
      },
      {
        id: "dispute-resolution",
        heading: "9. Dispute Resolution",
        paragraphs: [
          "Before filing any claim, you agree to contact us and attempt informal resolution for at least 30 days. If unresolved, disputes shall be settled by binding arbitration administered by the American Arbitration Association under its consumer rules, on an individual basis. You waive any right to participate in a class action. Either party may bring qualifying claims in small claims court.",
        ],
      },
      {
        id: "governing-law",
        heading: "10. Governing Law",
        paragraphs: [
          "These Terms are governed by the laws of the United States and the State of Delaware, without regard to conflict-of-law principles.",
        ],
      },
      {
        id: "termination",
        heading: "11. Termination of Access",
        paragraphs: [
          "We reserve the right to suspend or terminate access to the website at any time, with or without notice, for conduct that violates these Terms or is harmful to other users, us, or third parties.",
        ],
      },
      {
        id: "changes",
        heading: "12. Changes to These Terms",
        paragraphs: ["We may revise these Terms at any time. Continued use of the site after changes constitutes acceptance."],
      },
      {
        id: "contact",
        heading: "13. Contact",
        paragraphs: [`Questions about these Terms: ${CONTACT}.`],
      },
    ],
  },

  "refund-policy": {
    title: "Refund Policy",
    updated: "June 1, 2026",
    intro:
      "Important: ShoppinMagic does not sell any products. This page explains how refunds, returns, exchanges, and cancellations work for purchases made through links on our site.",
    sections: [
      {
        id: "not-a-seller",
        heading: "1. We Do Not Sell Products",
        paragraphs: [
          "ShoppinMagic is an affiliate marketplace. Every \"View on Merchant Site\" button on this site takes you to an external merchant (such as Amazon, Temu, or AliExpress) where your purchase takes place. We never process payments, hold inventory, or ship orders — so there is no transaction with us that we could refund.",
        ],
      },
      {
        id: "merchant-handles",
        heading: "2. Refunds Are Handled Only by the Merchant",
        paragraphs: [
          "All refunds, returns, exchanges, cancellations, and warranty claims are handled exclusively by the external merchant you purchased from, under that merchant's own policies.",
        ],
      },
      {
        id: "how-to-request",
        heading: "3. How to Request a Refund or Return",
        bullets: [
          "Find your order confirmation email from the merchant — it contains your order number and support links.",
          "Contact the merchant directly through their customer support channels (account page, help center, or support email).",
          "Follow the merchant's stated return policy — including return windows, condition requirements, and return shipping rules.",
          "Keep tracking numbers and correspondence until the refund is completed.",
        ],
      },
      {
        id: "what-we-cannot-do",
        heading: "4. What We Cannot Do",
        paragraphs: [
          "Because we are not the seller, ShoppinMagic cannot issue refunds, replacements, credits, or price adjustments; cancel or modify orders; or access your order details. If you contact us about a refund, the most we can do is point you to the correct merchant support channel.",
        ],
      },
      {
        id: "commissions",
        heading: "5. Affiliate Commissions Do Not Affect Your Price or Rights",
        paragraphs: [
          "When you buy through our links, the merchant pays us a commission from their own margin. This never increases your price and never reduces your refund or return rights — you receive exactly the same protections as any other customer of that merchant.",
        ],
      },
      {
        id: "policies-vary",
        heading: "6. Policies Vary by Merchant",
        paragraphs: [
          "Return windows, restocking fees, and refund methods differ between merchants and product categories. Always review the merchant's return policy before purchasing. If you need help finding a merchant's support page, email us at " + CONTACT + " and we'll help you locate it.",
        ],
      },
    ],
  },

  "cookie-policy": {
    title: "Cookie Policy",
    updated: "June 1, 2026",
    intro:
      "This Cookie Policy explains what cookies are, which ones ShoppinMagic uses, why, and how you can control them. It works together with our Privacy Policy and the consent banner shown on your first visit.",
    sections: [
      {
        id: "what-are-cookies",
        heading: "1. What Are Cookies?",
        paragraphs: [
          "Cookies are small text files stored on your device when you visit a website. They help sites function, remember preferences, measure traffic, and deliver relevant ads. We also use similar technologies like tracking pixels (tiny invisible images) for analytics and conversion measurement.",
        ],
      },
      {
        id: "cookies-we-use",
        heading: "2. Cookies We Use",
        bullets: [
          "Essential cookies — required for core site functionality such as navigation, search, and remembering your cookie consent choice. These cannot be disabled.",
          "Analytics cookies (Google Analytics) — measure visits, pages viewed, and site performance so we can improve content. IP anonymization is enabled where available.",
          "Advertising cookies (Google AdSense) — including Google's DoubleClick DART cookie — used to serve and personalize ads, limit how often you see an ad, and measure ad performance.",
          "Affiliate tracking cookies & pixels (Amazon Associates, Temu, AliExpress, CJ, Impact, ShareASale) — placed when you click an affiliate link so the merchant can credit us if you buy. These are typically set by the merchant/network domain.",
        ],
      },
      {
        id: "why",
        heading: "3. Why We Use Them",
        paragraphs: [
          "Cookies keep this site free: advertising and affiliate commissions are how ShoppinMagic earns revenue without charging you anything or selling your data. Analytics cookies tell us which guides and products are genuinely useful so we can make more of them.",
        ],
      },
      {
        id: "your-choices",
        heading: "4. Your Choices & Cookie Management",
        bullets: [
          "Consent banner — choose Accept All, Essential Only, or Decline on your first visit. To change your choice later, clear this site's data in your browser and the banner will reappear.",
          "Browser settings — block or delete cookies in Chrome, Safari, Firefox, or Edge settings. Blocking all cookies may affect site functionality.",
          "Google Ads Settings — adssettings.google.com to disable personalized ads.",
          "Industry opt-outs — www.aboutads.info/choices and optout.networkadvertising.org.",
          "Google Analytics opt-out add-on — tools.google.com/dlpage/gaoptout.",
        ],
      },
      {
        id: "gdpr-ccpa",
        heading: "5. GDPR & CCPA Compliance",
        paragraphs: [
          "For EEA/UK visitors (GDPR): non-essential cookies (analytics and advertising) are only activated after you give consent via our banner, and you may withdraw consent at any time. For California residents (CCPA/CPRA): we do not sell personal information; you may opt out of cookie-based sharing via the banner's \"Essential Only\" or \"Decline\" options. See our Privacy Policy for full details of your rights.",
        ],
      },
      {
        id: "changes-contact",
        heading: "6. Changes & Contact",
        paragraphs: [
          `We may update this Cookie Policy as our tools evolve; the date above shows the latest revision. Questions: ${CONTACT}.`,
        ],
      },
    ],
  },
};
